import { Box, Button, Grid, Table, TableBody, TableCell, TableRow } from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import { User } from "oidc-client";
import { useEffect } from "react";
import { Product } from "../../../api";
import { productDetails } from "../../../common/types";
import { useAppDispatch, useAppSelector } from "../../../store";
import { createReferralCodeAsync, getAdviserDetailsAsync, getReferralCodesAsync, saveClient } from "../../../store/adviser";
import { Colors, CopyToClipboardButton, SubtitleText, TitleText } from "../../controls/helpers";
import { getRoles } from "../Helpers";

const getAdviserProducts = (includeKiwiSaver?: boolean, includeWealthBuilder?: boolean, includeIncomeGenerator?: boolean, user?: User) => {
  const roles = getRoles(user);
  return [
    ...(roles.includes("KiwiSaver") && includeKiwiSaver ? [Product.KiwiSaver] : []),
    ...(roles.includes("WealthBuilder") && includeWealthBuilder ? [Product.WealthBuilder] : []),
    ...(roles.includes("IncomeGenerator") && includeIncomeGenerator ? [Product.IncomeGenerator] : []),
  ];
};

const SavingsAdviceTool = () => {
  const dispatch = useAppDispatch();
  const { isLoading, isSubmitting, referralCodes, adviserDetails } = useAppSelector((a) => a.adviser);
  const { user, isLoadingUser } = useAppSelector((state) => state.oidc);

  useEffect(() => {
    if (!adviserDetails?.adviserName) dispatch(getAdviserDetailsAsync());
    dispatch(getReferralCodesAsync());
    dispatch(saveClient(undefined));
  }, []);

  return (
    <Box px={11} py={3}>
      <TitleText>Savings Advice Tool</TitleText>
      <SubtitleText>
        Create a link to the Savings Advice Tool that you can email or text to prospective clients, if you have agreement from them to do so. The same link can be shared multiple
        times. It should not be shared on social media (eg, Facebook, LinkedIn).
        <br />
        <br />
        Alternatively, click &quot;Launch now&quot; to open the Savings Advice Tool directly in your browser.
        <br />
        <br />
        Clients that go through the Savings Advice Tool will be linked to you as the Adviser.
      </SubtitleText>
      {!isLoading && !isLoadingUser && !referralCodes?.length && (
        <Button
          variant="contained"
          color="primary"
          onClick={(_) => dispatch(createReferralCodeAsync({ includeKiwiSaver: true, includeWealthBuilder: true })).then(() => dispatch(getReferralCodesAsync()))}
          disabled={isSubmitting || isLoading}
        >
          Create link
        </Button>
      )}
      {!isLoading &&
        !isLoadingUser &&
        referralCodes?.map(({ adviserEmail, adviserGroup, adviserName, adviserNumber, includeKiwiSaver, includeWealthBuilder, includeIncomeGenerator, referralCode }) => (
          <Grid key={referralCode} container justifyContent="center" spacing={3}>
            <Grid item md={8} sm={11} xs={12}>
              <Box borderRadius="1.5rem" border={`1px solid ${Colors.Grey}`} bgcolor={Colors.White}>
                <Table size="small" padding="checkbox">
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ borderBottom: "unset", paddingTop: 10, fontWeight: 500 }}>Adviser number:</TableCell>
                      <TableCell style={{ borderBottom: "unset", paddingTop: 10 }}>{adviserNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ borderBottom: "unset", fontWeight: 500 }}>Adviser name:</TableCell>
                      <TableCell style={{ borderBottom: "unset" }}>{adviserName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ borderBottom: "unset", fontWeight: 500 }}>Adviser email:</TableCell>
                      <TableCell style={{ borderBottom: "unset" }}>{adviserEmail}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ borderBottom: "unset", fontWeight: 500 }}>Adviser company:</TableCell>
                      <TableCell style={{ borderBottom: "unset" }}>{adviserGroup}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 10, fontWeight: 500 }}>Products:</TableCell>
                      <TableCell style={{ paddingBottom: 10 }}>
                        {getAdviserProducts(includeKiwiSaver, includeWealthBuilder, includeIncomeGenerator, user)
                          .map((p) => productDetails[p].name)
                          .join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2} style={{ borderBottom: "unset" }}>
                        <Box my={1.5}>
                          <Grid container spacing={1}>
                            <Grid item sm={6} xs={12}>
                              <CopyToClipboardButton label="Copy link to clipboard" textToCopy={`${process.env.REACT_APP_URL}/invite/${referralCode}`} />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                              <Button
                                fullWidth
                                variant="contained"
                                color="secondary"
                                endIcon={<LaunchIcon />}
                                onClick={(_) => window.open(`${process.env.REACT_APP_URL}/invite/${referralCode}`, "_blank")}
                              >
                                Launch now
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Grid>
          </Grid>
        ))}
      <SubtitleText hideMarginBottom>
        <br />
        By using this link, you will:
        <ul style={{ margin: 0, paddingInlineStart: 30 }}>
          <li>consider if adviser disclosure statements need to be provided;</li>
          <li>comply with the Code of Professional Conduct for Financial Advice Services &amp; the Unsolicited Electronic Messages Act 2007.</li>
        </ul>
      </SubtitleText>
    </Box>
  );
};

export default SavingsAdviceTool;
